import React, { FC, useEffect, useState } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Checkbox from "../atoms/Checkbox";
import MainLayout from "../../Layouts/MainLayout";
import Label from "../atoms/Label";
import InputField from "../atoms/InputField";
import Button from "../atoms/Button";
import Spinner from "../atoms/Spinner";
import { toast } from 'react-toastify';
import { useNavigate, useParams } from "react-router-dom";
import { service } from "../../../services/AxiosServices";
import WindowHeightComponent from "../../Layouts/WindowHeight";
import SectionHeader from "../molecules/SectionHeader";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const CustomTabPanel: FC<TabPanelProps> = ({
  children,
  value,
  index,
  ...other
}) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`simple-tabpanel-${index}`}
    aria-labelledby={`simple-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{}}>{children}</Box>}
  </div>
);

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const RolePage: FC = () => {
  const [value, setValue] = useState(0);
  const [roleName, setRoleName] = useState("");
  const [clientErrors, setClientErrors] = useState<Record<string, any>>({});
  const [roleDetails, setRoleDetails] = useState<any>(null);
  const [roleKey, setRoleKey] = useState("");
  const [tabsData, setTabsData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { id } = useParams();
  const navigate = useNavigate();
  // Fetching permissions and role data on component mount or role id change
  useEffect(() => {
    const fetchTabsAndPermissions = async () => {
      try {
        setLoading(true);
  // Fetching permissions for all available tabs
        const response = await service<{ response: any[] }>("/permissions", "get");
        setTabsData(response.data.response || []);

        if (!id) return;
 // Fetching specific role data with permissions if editing an existing role
        const roleResponse = await service<{ response: any }>(
          "/role-with-permission-id",
          "post",
          { role_id: id }
        );

        const roleData = roleResponse.data.response;
        setRoleDetails(roleData);
        setRoleName(roleData.name);
        setRoleKey(roleData.key || roleData.name.toLowerCase().replace(/\s+/g, "_"));
       // Mapping permissions to tabs based on role data
        setTabsData((prev) =>
          prev.map((tab) => {
            const rolePermissions = roleData.permissions.find(
              (p: any) => p.classification_id === tab.id
            );

            if (rolePermissions) {
              return {
                ...tab,
                permissions: tab.permissions.map((perm: any) => {
                  const matchingPerm = rolePermissions.permissions.find(
                    (p: any) => p.permission_id === perm.id
                  );
                  return matchingPerm
                    ? {
                      ...perm,
                      create: matchingPerm.can_create,
                      read: matchingPerm.can_read,
                      edit: matchingPerm.can_update,
                      delete: matchingPerm.can_delete,
                    }
                    : perm;
                }),
              };
            }
            return tab;
          })
        );
      } catch (err) {
        console.error("Error fetching permissions and role details:", err);
        toast.error("Failed to fetch role details", { autoClose: 3000 });
      } finally {
        setLoading(false);
      }
    };

    fetchTabsAndPermissions();
  }, [id]);

  const validations = () => {
    let errors: Record<string, any> = {};
    if (roleName.trim().length === 0) {
      errors.roleName = 'Role name is required';
    }
    return errors;
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const handleRoleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setRoleName(name);
    setRoleKey(name.toLowerCase().replace(/\s+/g, "_"));
    setClientErrors({});
  };

  const handleCheckboxChange = (
    permissionType: "create" | "delete" | "edit" | "read",
    tabIndex: number,
    permissionIndex: number,
    checked: boolean
  ) => {
    setTabsData((prev) => {
      const updatedTabs = [...prev];
      const tabPermissions = updatedTabs[tabIndex].permissions;
      if (tabPermissions && tabPermissions[permissionIndex]) {
        tabPermissions[permissionIndex][permissionType] = checked;
      }
      return updatedTabs;
    });
  };

  const handleSave = async () => {
    const errors = validations();
    if (Object.keys(errors).length === 0) {
      const roleData = {
        id: id ? parseInt(id) : undefined,
        name: roleName,
        key: roleKey,
        permissions: tabsData.map((tab) => ({
          classification_id: tab.id,
          permissions: tab.permissions.map((perm: any) => ({
            permission_id: perm.id,
            can_create: perm.create,
            can_read: perm.read,
            can_update: perm.edit,
            can_delete: perm.delete,
          })),
        })),
      };

      try {
        setLoading(true);
        await service("/role-with-permissions/create-update", "post", roleData);
        toast.success(`Role ${id ? 'updated' : 'created'} successfully!`, { autoClose: 2000 });
        navigate("/role_details");
      } catch (err) {
        console.error(err);
        toast.error(`Failed to ${id ? 'update' : 'create'} the role. Please try again.`, { autoClose: 3000 });
      } finally {
        setLoading(false);
      }
    } else {
      setClientErrors({ ...errors });
    }
  };

  const renderPermissionsTable = (permissions: any[], tabIndex: number) => {
    return (
      <div className="permissions-table">
        {/* Headers Row */}
        <div className="row mb-3 border-bottom pb-2">
          <div className="col-4">
            <label className="LabelText fw-bold">Permission name</label>
          </div>
          <div className="col-8">
            <div className="d-flex justify-content-between px-4">
              <div className="text-center">
                <label className="LabelText fw-bold">Create</label>
              </div>
              <div className="text-center">
                <label className="LabelText fw-bold">Read</label>
              </div>
              <div className="text-center">
                <label className="LabelText fw-bold">Edit</label>
              </div>
              <div className="text-center">
                <label className="LabelText fw-bold">Delete</label>
              </div>
            </div>
          </div>
        </div>

        {/* Permission Rows */}
        {permissions.map((permission, index) => (
          <div className="row mb-3" key={index}>
            <div className="col-4">
              <label className="LabelText">{permission.name}</label>
            </div>
            <div className="col-8">
              <div className="d-flex justify-content-between px-4">
                <div className="text-center">
                  <Checkbox
                    checked={permission.create || false}
                    onChange={(checked: boolean) =>
                      handleCheckboxChange("create", tabIndex, index, checked)
                    }
                  />
                </div>
                <div className="text-center">
                  <Checkbox
                    checked={permission.read || false}
                    onChange={(checked: boolean) =>
                      handleCheckboxChange("read", tabIndex, index, checked)
                    }
                  />
                </div>
                <div className="text-center">
                  <Checkbox
                    checked={permission.edit || false}
                    onChange={(checked: boolean) =>
                      handleCheckboxChange("edit", tabIndex, index, checked)
                    }
                  />
                </div>
                <div className="text-center">
                  <Checkbox
                    checked={permission.delete || false}
                    onChange={(checked: boolean) =>
                      handleCheckboxChange("delete", tabIndex, index, checked)
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const clientErrorsArray = Object.entries(clientErrors).reduce((acc: string[], [field, error]) => {
    if (error) {
      acc.push(`${error}`);
    }
    return acc;
  }, []);

  return (
    <MainLayout>
      <SectionHeader  back = {true}title={id ? 'Edit role' : 'Create role'}></SectionHeader>
      <WindowHeightComponent className={""}>
        {clientErrorsArray.length > 0 && (
          <div className="alert alert-danger">
            <ul>
              {clientErrorsArray.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}
        {loading ? (
          <Spinner />
        ) : (
          <div className="p-3 mb-3">
            <Label htmlFor="roleName">Role name:<span style={{ color: 'red' }}>*</span></Label>
            <InputField
              id="roleName"
              name="roleName"
              value={roleName}
              onChange={handleRoleNameChange}
            />

            <Box sx={{ width: "100%" }}>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                  value={value}
                  onChange={handleChange}
                  aria-label="basic tabs example"
                >
                  {tabsData.map((tab, index) => (
                    <Tab
                      label={tab.name.charAt(0).toUpperCase() + tab.name.slice(1).toLowerCase()}
                      key={index}
                      {...a11yProps(index)}
                    />
                  ))}
                </Tabs>
              </Box>
              <div className="mt-3">
                {tabsData.map((tab, index) => (
                  <CustomTabPanel value={value} index={index} key={index}>
                    {/* <h3 className="permission-title">
                      {tab.name.charAt(0).toUpperCase() + tab.name.slice(1)}{" "}
                      permissions
                    </h3> */}
                    {renderPermissionsTable(tab.permissions || [], index)}
                  </CustomTabPanel>
                ))}
              </div>
            </Box>

            <div className="d-flex justify-content-end">
              {id && (
                <Button
                  className="btn SaveBtn me-2"
                  onClick={() => navigate('/role_details')}
                >
                  Cancel
                </Button>
              )}
              <Button className="btn SaveBtn" onClick={handleSave}>
                {id ? 'Update' : 'Save'}
              </Button>
            </div>
          </div>
        )}
      </WindowHeightComponent>
    </MainLayout>
  );
};

export default RolePage;